/**
 * Routing Status Service
 *
 * Provides BGP, OSPF, and BFD status data for edge devices
 */

import api from './api';

// ==========================================
// TYPES
// ==========================================

export interface BGPPeer {
  id: string;
  peerIp: string;
  asn: string;
  state: string;
  uptime: string;
  prefixReceived: number;
  prefixSent: number;
  lastUpdate: string;
  routerId: string;
}

export interface OSPFNeighbor {
  id: string;
  neighborId: string;
  interface: string;
  state: string;
  priority: string;
  deadTime: string;
  address: string;
  routeCount: number;
}

export interface BFDSession {
  id: string;
  peer: string;
  interface: string;
  state: string;
  detectTime: string;
  receivedPackets: number;
  sentPackets: number;
  uptime: string;
  interval: string;
}

export interface RoutingSummary {
  bgp: {
    totalPeers: number;
    established: number;
    prefixesRx: number;
    prefixesTx: number;
  };
  ospf: {
    totalNeighbors: number;
    fullState: number;
    interfaces: number;
  };
  bfd: {
    totalSessions: number;
    upSessions: number;
  };
}

// ==========================================
// API FUNCTIONS
// ==========================================

/**
 * Get BGP peers for an edge device
 */
export async function getBGPStatus(edgeId: string): Promise<BGPPeer[]> {
  const response = await api.get(`/routing-status/edges/${edgeId}/bgp`);
  return response.data?.data || response.data || [];
}

/**
 * Get OSPF neighbors for an edge device
 */
export async function getOSPFStatus(edgeId: string): Promise<OSPFNeighbor[]> {
  const response = await api.get(`/routing-status/edges/${edgeId}/ospf`);
  return response.data?.data || response.data || [];
}

/**
 * Get BFD sessions for an edge device
 */
export async function getBFDStatus(edgeId: string): Promise<BFDSession[]> {
  const response = await api.get(`/routing-status/edges/${edgeId}/bfd`);
  return response.data?.data || response.data || [];
}

/**
 * Get routing summary for an edge (combined stats)
 */
export async function getRoutingSummary(edgeId: string): Promise<RoutingSummary> {
  const response = await api.get(`/routing-status/edges/${edgeId}/summary`);
  return response.data?.data || response.data || {
    bgp: { totalPeers: 0, established: 0, prefixesRx: 0, prefixesTx: 0 },
    ospf: { totalNeighbors: 0, fullState: 0, interfaces: 0 },
    bfd: { totalSessions: 0, upSessions: 0 },
  };
}

export default {
  getBGPStatus,
  getOSPFStatus,
  getBFDStatus,
  getRoutingSummary,
};
